// Backend-slug → i18next-key maps for the profile screen's value enums.
// Web port of mobile/src/screens/main/profile-data-tables.ts (PR #272).
//
// Each map ties a backend slug (the value stored on
// `profile.{lifeStage,visaType,timeInCountry,ageRange}` or the entries
// of `INTEREST_KEYS`) to an i18n key. Render via
// `t(LIFE_STAGE_KEYS[slug])` at the call site; a slug missing from the
// map falls back to the raw slug at runtime.
//
// The values are typed as `string` (with `as const satisfies` to narrow
// each entry to its own string-literal type) rather than
// `Record<slug, ParseKeys>` because the typed-options ParseKeys union
// exploded past TypeScript's complexity threshold (TS2590) once both
// mobile + web key trees co-exist in shared/i18n/locales/en.json. The
// literal-narrowing form keeps `t(map[slug])` type-checked at the
// consumer site; typo safety on the values is restored statically by
// `shared/i18n/scripts/lint-call-sites.ts`, which scans every `_KEYS = {`
// declaration in web/src and validates the values against en.json.

export const LIFE_STAGE_KEYS = {
  student: 'settings.values.lifeStage.student',
  employed: 'settings.values.lifeStage.employed',
  job_seeking: 'settings.values.lifeStage.jobSeeking',
  retired: 'settings.values.lifeStage.retired',
  other: 'settings.values.lifeStage.other',
} as const satisfies Record<string, string>;

export const VISA_TYPE_KEYS = {
  work_permit: 'settings.values.visaType.workPermit',
  study_permit: 'settings.values.visaType.studyPermit',
  family_reunification: 'settings.values.visaType.familyReunification',
  eu_citizen: 'settings.values.visaType.euCitizen',
  permanent_resident: 'settings.values.visaType.permanentResident',
  other: 'settings.values.visaType.other',
} as const satisfies Record<string, string>;

export const TIME_IN_COUNTRY_KEYS = {
  less_than_6_months: 'settings.values.timeInCountry.lessThan6Months',
  '6_to_12_months': 'settings.values.timeInCountry.sixToTwelveMonths',
  '1_to_3_years': 'settings.values.timeInCountry.oneToThreeYears',
  '3_plus_years': 'settings.values.timeInCountry.threePlusYears',
} as const satisfies Record<string, string>;

export const AGE_RANGE_KEYS = {
  '18_24': 'settings.values.ageRange.r18_24',
  '25_34': 'settings.values.ageRange.r25_34',
  '35_44': 'settings.values.ageRange.r35_44',
  '45_54': 'settings.values.ageRange.r45_54',
  '55_plus': 'settings.values.ageRange.r55Plus',
} as const satisfies Record<string, string>;

export interface InterestOption {
  readonly key: string;
  readonly labelKey: string;
}

export const INTEREST_OPTIONS: readonly InterestOption[] = [
  { key: 'housing', labelKey: 'settings.values.interests.housing' },
  { key: 'healthcare', labelKey: 'settings.values.interests.healthcare' },
  { key: 'taxes', labelKey: 'settings.values.interests.taxes' },
  { key: 'social_life', labelKey: 'settings.values.interests.socialLife' },
  { key: 'career', labelKey: 'settings.values.interests.career' },
  { key: 'education', labelKey: 'settings.values.interests.education' },
  { key: 'legal', labelKey: 'settings.values.interests.legal' },
  { key: 'finance', labelKey: 'settings.values.interests.finance' },
  { key: 'family', labelKey: 'settings.values.interests.family' },
  { key: 'transportation', labelKey: 'settings.values.interests.transportation' },
];

export const INTEREST_LABEL_KEY_BY_SLUG: Record<string, string> = INTEREST_OPTIONS.reduce(
  (acc, { key, labelKey }) => ({ ...acc, [key]: labelKey }),
  {} as Record<string, string>,
);
